import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../../data/notification";
import useT from "../../i18n/useT";
import core from "../../core";

const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const thisMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const Earnings = () => {
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isStaff = role === "admin" || role === "moderator";

    const [range, setRange] = useState({ from: "", to: "" });
    const [report, setReport] = useState(null);

    const [staff, setStaff] = useState([]);
    const [salaries, setSalaries] = useState([]);
    const [periodFilter, setPeriodFilter] = useState("");
    const [form, setForm] = useState({ user_id: "", amount: "", period: thisMonth(), note: "" });
    const [saving, setSaving] = useState(false);
    const [updates, setUpdates] = useState(0);

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    const loadReport = useCallback(() => {
        const q = [];
        if (range.from) q.push(`from=${range.from}`);
        if (range.to) q.push(`to=${range.to}`);
        axios.get("/api/earnings" + (q.length ? "?" + q.join("&") : ""))
            .then((res) => setReport(res.data.data))
            .catch(() => {});
    }, [range.from, range.to]);

    useEffect(() => { if (isStaff) loadReport(); }, [isStaff, loadReport, updates]);

    useEffect(() => {
        if (!isStaff) return;
        axios.get("/api/salaries/staff").then((res) => setStaff(res.data.data || [])).catch(() => {});
    }, [isStaff]);

    useEffect(() => {
        if (!isStaff) return;
        axios.get("/api/salaries" + (periodFilter ? `?period=${periodFilter}` : ""))
            .then((res) => setSalaries(res.data.data || []))
            .catch(() => {});
    }, [isStaff, periodFilter, updates]);

    if (!isStaff) {
        return <div className="page-content-main-container"><h2 className="page-title">{t("earnings.title")}</h2></div>;
    }

    const submitSalary = (e) => {
        e.preventDefault();
        if (!form.user_id || form.amount === "" || Number(form.amount) <= 0) return;
        setSaving(true);
        axios.post("/api/salaries", form)
            .then((res) => { notify(res.data.message.en, "success-notification"); setForm({ user_id: "", amount: "", period: thisMonth(), note: "" }); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"))
            .finally(() => setSaving(false));
    };

    const deleteSalary = (id) => {
        if (!window.confirm(t("payroll.confirmDelete"))) return;
        axios.delete(`/api/salaries/${id}`)
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const staffName = (s) => (s.name ? `${s.name} ${s.surname || ""}`.trim() : s.username) + ` (${s.role})`;

    const tile = (label, value, accent) => (
        <div style={{ flex: "1 1 160px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px" }}>
            <div style={{ fontSize: "12px", opacity: 0.65, marginBottom: "6px" }}>{label}</div>
            <div style={{ fontSize: "22px", fontWeight: 700, color: accent || "var(--text-strong)" }}>{value}</div>
        </div>
    );

    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("earnings.title")}</h2>

            {/* Earnings report */}
            <h3 style={{ fontSize: "15px", margin: "6px 0 12px" }}>{t("earnings.report")}</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end", marginBottom: "16px" }}>
                <div>
                    <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("earnings.from")}</label>
                    <input type="date" className="form-control" value={range.from} onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))} />
                </div>
                <div>
                    <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("earnings.to")}</label>
                    <input type="date" className="form-control" value={range.to} onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))} />
                </div>
                {(range.from || range.to) && (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setRange({ from: "", to: "" })}>{t("earnings.clear")}</button>
                )}
            </div>

            {report && (
                <>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "22px" }}>
                        {tile(t("earnings.income"), money(report.income), "#34c38f")}
                        {tile(t("earnings.payroll"), money(report.payroll), "#ff9f0a")}
                        {tile(t("earnings.net"), money(report.net), report.net >= 0 ? "#2f80ed" : "#dc3545")}
                        {tile(t("earnings.transactions"), report.transactions)}
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: "22px", marginBottom: "28px" }}>
                        <div style={{ flex: "1 1 280px" }}>
                            <h4 style={{ fontSize: "13px", opacity: 0.8, marginBottom: "8px" }}>{t("earnings.byType")}</h4>
                            {report.byType.length === 0 ? <div style={{ opacity: 0.6 }}>{t("earnings.noData")}</div> : (
                                <table className="table" style={{ width: "100%" }}>
                                    <thead><tr><th>{t("earnings.type")}</th><th>{t("earnings.count")}</th><th>{t("earnings.amount")}</th></tr></thead>
                                    <tbody>{report.byType.map((r, i) => (
                                        <tr key={i}><td>{r.payment_type || "—"}</td><td>{r.cnt}</td><td>{money(r.total)}</td></tr>
                                    ))}</tbody>
                                </table>
                            )}
                        </div>
                        <div style={{ flex: "1 1 280px" }}>
                            <h4 style={{ fontSize: "13px", opacity: 0.8, marginBottom: "8px" }}>{t("earnings.byMonth")}</h4>
                            {report.byMonth.length === 0 ? <div style={{ opacity: 0.6 }}>{t("earnings.noData")}</div> : (
                                <table className="table" style={{ width: "100%" }}>
                                    <thead><tr><th>{t("earnings.month")}</th><th>{t("earnings.amount")}</th></tr></thead>
                                    <tbody>{report.byMonth.map((r, i) => (
                                        <tr key={i}><td>{r.ym}</td><td>{money(r.total)}</td></tr>
                                    ))}</tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Payroll */}
            <h3 style={{ fontSize: "15px", margin: "6px 0 12px" }}>{t("payroll.title")}</h3>
            <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px", marginBottom: "18px" }}>
                <h4 style={{ fontSize: "14px", marginBottom: "12px" }}>{t("payroll.record")}</h4>
                <form onSubmit={submitSalary} style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
                    <div style={{ flex: "2 1 200px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("payroll.employee")}</label>
                        <select className="form-control" value={form.user_id} onChange={(e) => setForm((p) => ({ ...p, user_id: e.target.value }))} required>
                            <option value="">{t("payroll.selectEmployee")}</option>
                            {staff.map((s) => <option key={s.id} value={s.id}>{staffName(s)}</option>)}
                        </select>
                    </div>
                    <div style={{ flex: "1 1 120px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("payroll.period")}</label>
                        <input type="month" className="form-control" value={form.period} onChange={(e) => setForm((p) => ({ ...p, period: e.target.value }))} required />
                    </div>
                    <div style={{ flex: "1 1 120px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("payroll.amount")}</label>
                        <input type="number" step="0.01" min="0" className="form-control" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} required />
                    </div>
                    <div style={{ flex: "2 1 160px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("payroll.note")}</label>
                        <input type="text" className="form-control" value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={saving}>{t("payroll.submit")}</button>
                </form>
            </div>

            <div style={{ display: "flex", alignItems: "flex-end", gap: "10px", marginBottom: "10px" }}>
                <div>
                    <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("payroll.filterPeriod")}</label>
                    <input type="month" className="form-control" value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)} />
                </div>
                {periodFilter && <button className="btn btn-sm btn-outline-secondary" onClick={() => setPeriodFilter("")}>{t("payroll.all")}</button>}
            </div>

            {salaries.length === 0 ? (
                <div style={{ opacity: 0.6 }}>{t("payroll.none")}</div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="table" style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                <th>{t("payroll.employee")}</th>
                                <th>{t("payroll.period")}</th>
                                <th>{t("payroll.amount")}</th>
                                <th>{t("payroll.note")}</th>
                                <th>{t("payroll.paidBy")}</th>
                                <th>{t("payroll.date")}</th>
                                <th>{t("payroll.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {salaries.map((s) => (
                                <tr key={s.id}>
                                    <td>{s.name ? `${s.name} ${s.surname || ""}`.trim() : s.username || s.user_id}</td>
                                    <td>{s.period}</td>
                                    <td>{money(s.amount)}</td>
                                    <td>{s.note || "—"}</td>
                                    <td>{s.paid_by || "—"}</td>
                                    <td>{s.paid_at ? core.formdatDate(s.paid_at, "dd-mm-yyyy") : "—"}</td>
                                    <td><button className="btn btn-sm btn-danger" onClick={() => deleteSalary(s.id)}>{t("payroll.delete")}</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default Earnings;
